import { useEffect, useState } from 'react'
import type { StageId } from '@joker/domain'

/**
 * Where the console is, as an address. The URL is the one source of truth for
 * which screen is open and with which filter, so Back and Forward walk the
 * user's own path and any screen — a person, an order, a program, a filtered
 * patient list — can be copied and sent.
 *
 *   /pipeline  /cohorts  /retention  /stats  /usage  /leads  /purchases
 *   /patients?bucket=…&stage=…&practice=…&age=…&paidIn=…&segment=…&trained=…&paid=1
 *   /leads?bucket=…   /optometrists  /jobs  /health
 *   /cohorts/:id   /cohorts/compare   (/buckets, the old name, opens /cohorts)
 *   /people/:id   /purchases/:orderId   /practices/:id   /programs/:id
 */

export type Tab =
  | 'pipeline'
  | 'cohorts'
  | 'retention'
  | 'stats'
  | 'usage'
  | 'prospects'
  | 'purchases'
  | 'people'
  | 'optometrists'
  | 'jobs'
  | 'health'

export type TrainedWithin = 'ever' | 'm6' | 'm3' | 'm1' | 'w2' | 'w1' | 'today' | 'yesterday'

/** The Patients table's question. Each is one way of asking; opening one clears the rest. */
export interface PatientFilters {
  bucketId?: string
  stage?: StageId
  practiceId?: string
  ageBand?: string
  paidIn?: string
  segment?: number
  trainedWithin?: TrainedWithin
  paidOnly?: boolean
}

export type Route =
  | { page: 'tab'; tab: Tab; filters?: PatientFilters; leadBucketId?: string }
  | { page: 'person'; id: string }
  | { page: 'order'; id: string }
  | { page: 'practice'; id: string }
  | { page: 'program'; id: string }
  /** One cohort's page, with what can be done with it. */
  | { page: 'cohort'; id: string }
  /** The automatic splits (channel, month, age…) against the training milestones. */
  | { page: 'cohortCompare' }

/* The address names a screen the way people say it, not the way the code does. */
const SLUG: Record<Tab, string> = {
  pipeline: 'pipeline',
  cohorts: 'cohorts',
  retention: 'retention',
  stats: 'stats',
  usage: 'usage',
  prospects: 'leads',
  purchases: 'purchases',
  people: 'patients',
  optometrists: 'optometrists',
  jobs: 'jobs',
  health: 'health',
}
const TAB_OF = {
  ...(Object.fromEntries(Object.entries(SLUG).map(([t, s]) => [s, t as Tab])) as Record<string, Tab>),
  /* Buckets and cohorts became one thing; old links still land. */
  buckets: 'cohorts',
} as Record<string, Tab>

const TRAINED: readonly TrainedWithin[] = ['ever', 'm6', 'm3', 'm1', 'w2', 'w1', 'today', 'yesterday']
const ID = /^[0-9a-f-]{36}$/i

export function parse(path: string, search: string): Route {
  const parts = path.split('/').filter(Boolean)
  const q = new URLSearchParams(search)
  const [first, second] = parts

  if (first === 'people' && second && ID.test(second)) return { page: 'person', id: second }
  if (first === 'practices' && second && ID.test(second)) return { page: 'practice', id: second }
  if (first === 'programs' && second && ID.test(second)) return { page: 'program', id: second }
  if (first === 'purchases' && second && ID.test(second)) return { page: 'order', id: second }
  if (first === 'cohorts' && second === 'compare') return { page: 'cohortCompare' }
  if (first === 'cohorts' && second && ID.test(second)) return { page: 'cohort', id: second }

  const tab = TAB_OF[first ?? ''] ?? 'pipeline'
  if (tab === 'prospects') {
    const b = q.get('bucket')
    return b ? { page: 'tab', tab, leadBucketId: b } : { page: 'tab', tab }
  }
  if (tab !== 'people') return { page: 'tab', tab }

  const f: PatientFilters = {}
  const str = (k: string) => q.get(k) || undefined
  if (str('bucket')) f.bucketId = str('bucket')
  if (str('stage')) f.stage = str('stage') as StageId
  if (str('practice')) f.practiceId = str('practice')
  if (str('age')) f.ageBand = str('age')
  if (str('paidIn')) f.paidIn = str('paidIn')
  const seg = q.get('segment')
  if (seg != null && seg !== '' && Number.isFinite(Number(seg))) f.segment = Number(seg)
  const tw = q.get('trained')
  if (tw && (TRAINED as readonly string[]).includes(tw)) f.trainedWithin = tw as TrainedWithin
  if (q.get('paid') === '1') f.paidOnly = true
  return { page: 'tab', tab, filters: f }
}

export function format(r: Route): string {
  switch (r.page) {
    case 'person': return `/people/${r.id}`
    case 'order': return `/purchases/${r.id}`
    case 'practice': return `/practices/${r.id}`
    case 'program': return `/programs/${r.id}`
    case 'cohort': return `/cohorts/${r.id}`
    case 'cohortCompare': return '/cohorts/compare'
  }
  const q = new URLSearchParams()
  if (r.tab === 'prospects' && r.leadBucketId) q.set('bucket', r.leadBucketId)
  const f = r.filters ?? {}
  if (f.bucketId) q.set('bucket', f.bucketId)
  if (f.stage) q.set('stage', f.stage)
  if (f.practiceId) q.set('practice', f.practiceId)
  if (f.ageBand) q.set('age', f.ageBand)
  if (f.paidIn) q.set('paidIn', f.paidIn)
  if (f.segment != null) q.set('segment', String(f.segment))
  if (f.trainedWithin) q.set('trained', f.trainedWithin)
  if (f.paidOnly) q.set('paid', '1')
  const qs = q.toString()
  return `/${SLUG[r.tab]}${qs ? `?${qs}` : ''}`
}

/** The tab a page belongs under, so the right one stays lit in the menu. */
export function tabOf(r: Route): Tab {
  switch (r.page) {
    case 'tab': return r.tab
    case 'order': return 'purchases'
    case 'practice': return 'optometrists'
    case 'cohort':
    case 'cohortCompare': return 'cohorts'
    default: return 'people'
  }
}

/* Each entry this console pushes carries its depth, so "← back" can tell a
   page reached from inside Joker (go back) from one opened from a pasted link
   (there is nothing to go back to; open its parent instead). */
interface HistoryState {
  depth: number
}

/**
 * The current route, and a way to move. Moving pushes a history entry, so the
 * browser's Back walks the path the user took; the browser's own Back and
 * Forward land here through popstate.
 */
export function useRoute(): {
  route: Route
  navigate: (r: Route, opts?: { replace?: boolean }) => void
  back: (fallback: Route) => void
} {
  const read = () => parse(window.location.pathname, window.location.search)
  const [route, setRoute] = useState<Route>(read)

  useEffect(() => {
    // The bare root has no screen of its own; name the one it opens.
    if (window.location.pathname === '/' || !window.history.state) {
      window.history.replaceState({ depth: 0 } satisfies HistoryState, '', format(read()))
    }
    const onPop = () => {
      setRoute(read())
      window.scrollTo(0, 0)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const navigate = (r: Route, opts: { replace?: boolean } = {}) => {
    const url = format(r)
    const depth = ((window.history.state as HistoryState | null)?.depth ?? 0) + (opts.replace ? 0 : 1)
    if (url !== window.location.pathname + window.location.search) {
      if (opts.replace) window.history.replaceState({ depth } satisfies HistoryState, '', url)
      else window.history.pushState({ depth } satisfies HistoryState, '', url)
    }
    setRoute(r)
    if (!opts.replace) window.scrollTo(0, 0)
  }

  const back = (fallback: Route) => {
    if (((window.history.state as HistoryState | null)?.depth ?? 0) > 0) window.history.back()
    else navigate(fallback, { replace: true })
  }

  return { route, navigate, back }
}
